import type { IsoFinesClass, SoilFractionsContract } from "@/contracts";
import { roundToPrecision } from "@/utils";
import { subfractionWord } from "./subfraction";
import type { SoilDescriptionResult } from "./types";

type Constituent = "clay" | "silt" | "sand" | "gravel";

const ADJECTIVE: Record<Constituent, string> = {
  clay: "clayey",
  silt: "silty",
  sand: "sandy",
  gravel: "gravelly",
};

interface Modifier {
  constituent: Constituent;
  percentage: number;
}

function secondaryCoarseWord({ constituent, percentage }: Modifier): string | null {
  if (percentage < 1) return null;
  if (percentage < 5) return `slightly ${ADJECTIVE[constituent]}`;
  if (percentage <= 20) return ADJECTIVE[constituent];
  return `very ${ADJECTIVE[constituent]}`;
}

function secondaryFineWord({ constituent, percentage }: Modifier): string | null {
  if (percentage < 1) return null;
  if (percentage < 35) return `slightly ${ADJECTIVE[constituent]}`;
  if (percentage <= 65) return ADJECTIVE[constituent];
  return `very ${ADJECTIVE[constituent]}`;
}

export function isoGradingWord(cu: number | null, cc: number | null): string | null {
  if (cu == null || cc == null || !Number.isFinite(cu) || !Number.isFinite(cc)) return null;
  if (cu < 3 && cc < 1) return "uniformly graded";
  if (cu >= 3 && cu <= 6 && cc < 1) return "poorly graded";
  if (cu >= 6 && cu <= 15 && cc < 1) return "medium graded";
  if (cu > 15 && cc >= 1 && cc <= 3) return "well graded";
  if (cu > 15 && cc < 0.5) return "gap graded";
  return null;
}

function excludingCobbles(f: SoilFractionsContract): SoilFractionsContract {
  const total = f.clay + f.silt + f.sand + f.gravel;
  const share = (value: number) => roundToPrecision((value * 100) / total);
  return { ...f, clay: share(f.clay), silt: share(f.silt), sand: share(f.sand), gravel: share(f.gravel), cobbles: 0 };
}

interface FinerMaterial {
  head: string;
  primary: string;
  grading: string | null;
}

function describeFinerMaterial(f: SoilFractionsContract, finesClass: IsoFinesClass | null): FinerMaterial {
  const join = (words: (string | null)[]) => words.filter((w): w is string => w != null).join(" ");
  const fines = f.clay + f.silt;

  if (fines >= 35) {
    const primary = finesClass ?? "CLAY";
    const head = join([
      secondaryFineWord({ constituent: "gravel", percentage: f.gravel }),
      secondaryFineWord({ constituent: "sand", percentage: f.sand }),
      primary,
    ]);
    return { head, primary, grading: null };
  }

  const finesConstituent =
    finesClass === "CLAY" ? "clay" : finesClass === "SILT" ? "silt" : f.clay > f.silt ? "clay" : "silt";
  const finesWord = secondaryCoarseWord({ constituent: finesConstituent, percentage: fines });
  const grading = isoGradingWord(f.cu, f.cc);

  if (Math.round(f.sand) === Math.round(f.gravel)) {
    return { head: join([finesWord, "SAND and GRAVEL"]), primary: "SAND and GRAVEL", grading };
  }

  if (f.sand > f.gravel) {
    const subfraction = subfractionWord({ fine: f.fineSand, medium: f.mediumSand, coarse: f.coarseSand });
    const gravelWord = secondaryCoarseWord({ constituent: "gravel", percentage: f.gravel });
    return { head: join([gravelWord, finesWord, subfraction, "SAND"]), primary: "SAND", grading };
  }

  const subfraction = subfractionWord({ fine: f.fineGravel, medium: f.mediumGravel, coarse: f.coarseGravel });
  const sandWord = secondaryCoarseWord({ constituent: "sand", percentage: f.sand });
  return { head: join([sandWord, finesWord, subfraction, "GRAVEL"]), primary: "GRAVEL", grading };
}

function amountOfFinerMaterial(percentage: number): string {
  if (percentage < 5) return "a little";
  if (percentage <= 20) return "some";
  return "much";
}

function cobbleContent(percentage: number): string {
  if (percentage < 5) return "low";
  if (percentage <= 20) return "medium";
  return "high";
}

export function describeSoilIso(
  measured: SoilFractionsContract,
  finesClass: IsoFinesClass | null,
): SoilDescriptionResult | null {
  const belowCobbles = measured.clay + measured.silt + measured.sand + measured.gravel;
  const total = belowCobbles + measured.cobbles;
  if (total <= 0) return null;
  if (belowCobbles <= 0) return { description: "COBBLES", classification: "COBBLES" };

  const cobbles = roundToPrecision((measured.cobbles * 100) / total);
  const finer = describeFinerMaterial(excludingCobbles(measured), finesClass);

  if (cobbles > 50) {
    return {
      description: `COBBLES with ${amountOfFinerMaterial(roundToPrecision(100 - cobbles))} ${finer.head}`,
      classification: "COBBLES",
    };
  }

  const head = cobbles >= 1 ? `${finer.head} with ${cobbleContent(cobbles)} cobble content` : finer.head;
  return { description: finer.grading ? `${head}, ${finer.grading}` : head, classification: finer.primary };
}
